import { close } from "fs";
import React, {
  FC,
  Fragment,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
} from "react";
import styled from "styled-components";
import { getDistance } from "../functions/getDistance";
import lang from "../functions/lang";
import { levenSort } from "../functions/levenSort";
import { locationSearch } from "../functions/locationSearch";
import { sortStores } from "../functions/sortStores";
import { Store } from "../interfaces/IStore";
import { AppContext } from "./AppContext";
import SearchIcon from "./Icons/SearchIcon";
import { MapContext } from "./MapContext";

const SearchBarInputContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;
// padding: 0 2rem;

const SearchBarInput = styled.input`
  padding: 11px;
  padding-top: 15px;
  width: 100%;
  border-radius: 28px;
  border: 1px solid #c0bebe;
  box-sizing: border-box;
  background-color: #fff;
  ::placeholder {
    padding-left: 5px;
    padding-top: 3px;
    font-size: 15px;
    color: #757575;
  }
`;

const LocationSearchButton = styled.button`
  border-radius: 36px;
  background-color: #522398;
  color: #fff;
  width: 68px;
  position: absolute;
  font-size: 11px;
  right: 16px;
  height: 87%;
  outline: none;
  border: none;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.38;
  padding-top: 3px;
`;

const LocationSearchIcon = styled.div`
  position: absolute;
  right: 32px;
`;

// 0.1rem
const SearchBarInputInnerContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const LocationSearchIconElement = styled.div`
  width: 23px;
  height: 24px;
  position: relative;
  top: 2px;
  left: 9px;
`;

interface ISearchProps {
  viewPort: string;
  openFilter?: () => void;
  setSearchError: (error: boolean) => void;
}

export const SearchBar: FC<PropsWithChildren<ISearchProps>> = ({
  viewPort,
  openFilter,
  setSearchError,
}) => {
  const { setCoords, setSortCoords, stores, setDefaultZoom } = useContext(
    MapContext
  );

  const keyUpListener = (e: KeyboardEvent) => {
    const selectedKey = e.key.toLowerCase();
    if (selectedKey === "enter" || selectedKey === "return") {
      handleLocationSearch();
    }
  };

  const searchInputRef = useRef<HTMLInputElement>(null);
  const handleLocationSearch = async () => {
    let query = searchInputRef.current?.value;

    if (searchInputRef.current && query) {
      const leven = levenSort(query, stores);
      if (leven) {
        setDefaultZoom(15);
        setCoords(leven);
        setSortCoords(leven);
      } else {
        const result = await locationSearch(query);
        if (result) {
          const closestStore = sortStores(result, stores)[0];
          const distance = getDistance(
            [closestStore.latitude, closestStore.longitude],
            [result.lat, result.lng]
          );
          switch (true) {
            case distance <= 50000:
              setDefaultZoom(11);
              setCoords(result);
              break;
            default:
              setSearchError(true);
              setDefaultZoom(10);
              setCoords(result);
              break;
          }
          setSortCoords(result);
        }
      }
      openFilter && openFilter();
    } else {
      navigator.geolocation.getCurrentPosition((res) => {
        const closestStore = sortStores(
          {
            lat: res.coords.latitude,
            lng: res.coords.longitude,
          },
          stores
        )[0];
        const mapTarget = {
          lat: closestStore.latitude,
          lng: closestStore.longitude,
        };
        setCoords(mapTarget);
        setSortCoords(mapTarget);
        const distance = getDistance(
          [res.coords.latitude, res.coords.longitude],
          [closestStore.latitude, closestStore.longitude]
        );
        setDefaultZoom(distance / 1500);
      });
    }

    window.removeEventListener("keyup", keyUpListener);
  };

  // Function that will see if the search input is the one that has been clicked
  const determineIfClicked = () => {
    if (document.activeElement == searchInputRef.current) {
      // Add event listener to the window
      window.addEventListener("keyup", keyUpListener);
    }
  };

  const SearchInputRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    if (viewPort == "mobile") {
      SearchInputRef.current.style.padding = "0";
    } else {
      SearchInputRef.current.style.padding = "0 0.8rem";
    }
  }, []);

  return (
    <Fragment>
      <SearchBarInputContainer>
        <SearchBarInputInnerContainer ref={SearchInputRef}>
          {viewPort == "mobile" ? (
            <SearchBarInput
              style={{
                padding: "16px",
                boxShadow: "4px 0px 15px rgba(0, 0, 0, 0.25)",
              }}
              ref={searchInputRef}
              onClick={() => determineIfClicked()}
              placeholder={lang(
                "modal.search_placeholder",
                window.data?.language || "en"
              )}
            />
          ) : (
            <SearchBarInput
              style={{ padding: "11px" }}
              ref={searchInputRef}
              onClick={() => determineIfClicked()}
              placeholder={lang(
                "modal.search_placeholder",
                window.data?.language || "en"
              )}
            />
          )}

          {viewPort == "mobile" ? (
            <LocationSearchIcon onClick={handleLocationSearch}>
              <LocationSearchIconElement>
                <SearchIcon />
              </LocationSearchIconElement>
            </LocationSearchIcon>
          ) : (
            <LocationSearchButton onClick={handleLocationSearch}>
              {lang("modal.searchButton", window.data?.language || "en")}
            </LocationSearchButton>
          )}
        </SearchBarInputInnerContainer>
      </SearchBarInputContainer>
    </Fragment>
  );
};
